/* eslint-disable @typescript-eslint/no-shadow */
import { useState, useMemo } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';

import { ApplicationType, AppInfo, Application, PlanEnum } from 'api-bible-shared-lib/utils/types';
import {
  appNameValidator,
  appDescriptionValidator,
  appTypeValidator,
} from 'api-bible-shared-lib/utils/validation';
import { Alert, AlertType } from './Alert';
import { Button, ButtonLink } from './Button';
import { InputGroup, InputImage, InputSelect } from './Inputs';
import { IconDownChevron } from './Icons';
import FormHeader from './FormHeader';
import { InputCheckboxTermsAndConditions } from './TermsAndConditionsModal';

import { useAccountUser } from '../contexts/User';

interface FormState extends Omit<AppInfo, 'type'> {
  type: ApplicationType | '';
  acceptTermsAndConditions?: string;
}

const defaultSchema = {
  name: appNameValidator.clone(),
  description: appDescriptionValidator.clone(),
  marketingUrl: yup.string().url(),
  type: appTypeValidator.clone(),
  iconPath: yup.string(),
};

const termsSchema = {
  acceptTermsAndConditions: yup.string().required('Accepting the Terms and Conditions is required'),
};

const defaultInitialValues: FormState = {
  name: '',
  description: '',
  marketingUrl: '',
  type: '',
  iconPath: '',
  plan: PlanEnum.FREE,
  acceptTermsAndConditions: '',
};

interface ApplicationFormProps {
  initialAppValues?: Partial<Application> | undefined;
  backUrl?: string;
  handleSubmit: (appFormData: AppInfo) => Promise<Application | void>;
  submitText: string | React.ReactNode;
  requireTermsAndConditions?: boolean;
}

export default function ApplicationForm({
  initialAppValues,
  backUrl,
  handleSubmit,
  submitText,
  requireTermsAndConditions = false,
}: ApplicationFormProps) {
  const { account } = useAccountUser();
  const initialValues = (Object.keys(defaultInitialValues) as Array<keyof FormState>).reduce(
    (initValues, key) => {
      // @ts-ignore
      // eslint-disable-next-line no-param-reassign
      initValues[key] = initialAppValues?.[key] || defaultInitialValues[key];
      return initValues;
    },
    {} as FormState,
  );
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        ...defaultSchema,
        ...(requireTermsAndConditions ? termsSchema : {}),
      }),
    [requireTermsAndConditions],
  );

  const [error, setError] = useState<Error | undefined>();

  const onSubmit = async (values: FormState, { setSubmitting }: any) => {
    setError(undefined);

    try {
      // NOTE: type my be set here because formik will validate again yup schema
      // this however makes typescript happy
      const type = values.type || ApplicationType.other;
      await handleSubmit({ ...values, type });
    } catch (e) {
      const err = e as Error;
      setError(err);
    }

    setSubmitting(false);
  };

  if (!account) return null;

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, isSubmitting, values }) => (
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert
              className="mb-8"
              type={AlertType.ERROR}
              title="There was an error with your submission"
              message={error?.message || 'An unexpected error occurred.'}
            />
          )}

          <FormHeader />

          <div className="mb-8">
            <InputImage
              name="iconPath"
              defaultSrc={initialAppValues?.iconPath}
              storagePrefix={account.id}
              shape="square"
            >
              <svg
                className="w-14 h-14"
                viewBox="0 0 50 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M39.0645 17.7085L24.9999 3.646L10.9395 17.7085H18.7499V31.771C18.7499 32.1854 18.9145 32.5828 19.2075 32.8759C19.5005 33.1689 19.898 33.3335 20.3124 33.3335H29.6874C30.1018 33.3335 30.4992 33.1689 30.7922 32.8759C31.0852 32.5828 31.2499 32.1854 31.2499 31.771V17.7085H39.0645Z"
                  stroke="#3A83FF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M48.4395 36.4585V42.7085C48.4395 43.5373 48.1102 44.3322 47.5242 44.9182C46.9381 45.5043 46.1433 45.8335 45.3145 45.8335H4.68945C3.86065 45.8335 3.0658 45.5043 2.47974 44.9182C1.89369 44.3322 1.56445 43.5373 1.56445 42.7085V36.4585"
                  stroke="#3A83FF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </InputImage>
          </div>

          <div className="w-full">
            <InputGroup
              name="name"
              label="Application Name"
              placeholder="Your application's name"
            />
            <InputGroup
              name="marketingUrl"
              label="Marketing Url"
              placeholder="https://example.com"
            />
            <InputSelect name="type" label="Application Type" placeholder="Type of application">
              {Object.values(ApplicationType).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </InputSelect>
            <InputGroup
              name="description"
              label="Description"
              as="textarea"
              placeholder="A short description of your application"
              rows="3"
            />

            {requireTermsAndConditions && typeof values.acceptTermsAndConditions === 'string' && (
              <InputCheckboxTermsAndConditions
                value={values.acceptTermsAndConditions}
                name="acceptTermsAndConditions"
              />
            )}

            <div className="flex flex-row-reverse justify-between">
              <Button outline disabled={isSubmitting} loading={isSubmitting} type="submit">
                {submitText}
              </Button>
              {backUrl && (
                <ButtonLink to={backUrl} outline type="submit">
                  <IconDownChevron className="w-6 h-6 transform rotate-90" />
                  Back
                </ButtonLink>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}
